/* eslint-disable react/no-unused-prop-types */
import React, { useState, useEffect } from 'react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import { useInView } from 'react-intersection-observer';
import PropTypes from 'prop-types';
import get from 'lodash.get';


import Related from 'components/Related';
import GiftGuide from 'components/GiftGuide';
import ErrorBoundary from 'components/ErrorBoundary';

import { fetchAndSetFormattedItems, shouldShowRelatedContent } from './utils';

const mapStateToProps = (state) => {
  const taxonomy = get(state, 'article.content[0].taxonomy');
  const articleID = get(state, 'article.content[0].id');
  return {
    taxonomy,
    articleID,
  };
};

/**
 * Render function that checks props to determine if we need to render a Gift Guide component or a
 * Related component. If no items are provided to render, returns null.
 */
const getRecoComponent = (props, formattedItems) => {
  const {
    additionalClasses,
    isGiftGuide,
    isNBCNewsCommerce,
    isBetterNewsCommerce,
    isRightRail,
    isTrending,
  } = props;

  // No results, return early
  if (!formattedItems.length) {
    return null;
  }

  const title = isTrending ? i18next.t('Trending') : i18next.t('Recommended');
  const id = title.toLowerCase();

  let linkTrackingID;
  if (isTrending || isNBCNewsCommerce) {
    linkTrackingID = 'recommended_shop';
  } else {
    linkTrackingID = id;
  }

  if (isGiftGuide) {
    return (
      <GiftGuide
        items={formattedItems}
        data-test="gift-guide"
      />
    );
  }

  if (shouldShowRelatedContent(props)) {
    return (
      <Related
        additionalClasses={additionalClasses}
        elementId={id}
        isNBCNewsCommerce={isNBCNewsCommerce}
        isBetterNewsCommerce={isBetterNewsCommerce}
        isRightRail={isRightRail}
        isTrending={isTrending}
        items={formattedItems}
        linkTracking={linkTrackingID}
        title={title}
      />
    );
  }

  return null;
};

const Recommended = (props) => {
  const { isRightRail, lazyLoad } = props;
  const [formattedItems, setFormattedItems] = useState([]);

  const { ref, inView } = useInView({
    initialInView: !lazyLoad,
    triggerOnce: true,
    // The footer gift guide widget can start loading earlier, since users need to scroll down to it
    rootMargin: isRightRail ? '' : '60px 0px',
  });
  useEffect(() => {
    if (inView) {
      fetchAndSetFormattedItems(props, setFormattedItems);
    }
  }, [inView]);

  const renderedComponent = getRecoComponent(props, formattedItems);

  return (
    <div className="recommended-intersection-ref" ref={ref}>
      {renderedComponent}
    </div>
  );
};

Recommended.propTypes = {
  additionalClasses: PropTypes.string,
  articleID: PropTypes.string,
  isGiftGuide: PropTypes.bool,
  isNBCNewsCommerce: PropTypes.bool,
  isBetterNewsCommerce: PropTypes.bool,
  isRightRail: PropTypes.bool,
  isTrending: PropTypes.bool,
  lazyLoad: PropTypes.bool,
  mobile: PropTypes.bool,
  taxonomy: PropTypes.shape({
    primarySection: PropTypes.shape({
      id: PropTypes.string,
    }),
    primaryTopic: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

Recommended.defaultProps = {
  additionalClasses: '',
  isGiftGuide: false,
  isNBCNewsCommerce: false,
  isBetterNewsCommerce: false,
  isRightRail: false,
  isTrending: false,
  lazyLoad: true,
  mobile: true,
  taxonomy: {},
  articleID: '',
};

const WrappedRecommended = connect(mapStateToProps)(Recommended);

function SafeRecommended(props) {
  return (
    <ErrorBoundary>
      <WrappedRecommended {...props} />
    </ErrorBoundary>
  );
}

// used for debugging (and also a brittle test)
SafeRecommended.displayName = 'SafeRecommended(Connect(Recommended))';

export default SafeRecommended;
